// <product-bundle> — multi-group bundle builder.
// Selection is held in memory; "Add all to cart" posts one /cart/add.js call
// with an items array, then optionally applies a merchant discount code.
class ProductBundle extends HTMLElement {
  connectedCallback() {
    this.currency = this.dataset.currency || '$';
    this.moneyFormat = this.dataset.moneyFormat || '';
    this.discountCode = this.dataset.discountCode || '';
    this.minItems = parseInt(this.dataset.minItems, 10) || 0;
    this.onceOnly = this.dataset.onceOnly === 'true';
    this.bundleLabel = this.dataset.bundleLabel || 'Bundle';
    this.bundlePropertyName = this.dataset.bundlePropertyName || 'Bundle';
    this.addLabel = this.dataset.addLabel || 'Add to bundle';
    this.addedLabel = this.dataset.addedLabel || 'Added';

    // Tier thresholds: [{ min: 3, label: '5% off' }, ...]
    try {
      this.tiers = JSON.parse(this.dataset.tiers || '[]');
    } catch (e) {
      this.tiers = [];
    }

    // selection: Map<variantId, { id, quantity, price, title, group }>
    this.selection = new Map();

    // Persist the in-progress bundle across page navigation. sessionStorage
    // (not localStorage) so it clears when the tab closes — a half-built
    // bundle restored days later would show stale prices.
    this.storageKey = 'neroli-bundle-' + (this.dataset.sectionId || 'default');
    this.storageMaxAgeMs = 6 * 60 * 60 * 1000; // 6 hours

    this.bindTabs();
    this.bindVariantPickers();
    this.bindQuantitySteppers();
    this.bindAddButtons();
    this.bindSubmit();
    this.bindMobileSheet();
    this.bindBarVisibility();
    this.restoreSelection();
    this.render();
  }

  // ---- Persistence ------------------------------------------------------
  saveSelection() {
    try {
      if (this.selection.size === 0) {
        sessionStorage.removeItem(this.storageKey);
        return;
      }
      const payload = {
        savedAt: Date.now(),
        items: Array.from(this.selection.values()),
      };
      sessionStorage.setItem(this.storageKey, JSON.stringify(payload));
    } catch (e) {
      // Storage can be unavailable (private mode, quota). Non-fatal — the
      // builder still works, it just won't survive navigation.
    }
  }

  restoreSelection() {
    let payload;
    try {
      const raw = sessionStorage.getItem(this.storageKey);
      if (!raw) return;
      payload = JSON.parse(raw);
    } catch (e) {
      return;
    }

    if (!payload || !Array.isArray(payload.items)) return;

    // Expire stale bundles rather than restoring old prices.
    if (payload.savedAt && Date.now() - payload.savedAt > this.storageMaxAgeMs) {
      try { sessionStorage.removeItem(this.storageKey); } catch (e) {}
      return;
    }

    // Only restore items whose product cards still exist on this page — a
    // merchant may have changed the collections since the bundle was started.
    payload.items.forEach((entry) => {
      const card = this.findCardForVariant(entry.id);
      if (!card) return;

      this.selection.set(entry.id, entry);
      card.dataset.selectedVariant = entry.id;
      card.classList.add('is-selected');

      const qtyInput = card.querySelector('[data-bundle-qty-input]');
      if (qtyInput) qtyInput.value = entry.quantity;

      const variantSelect = card.querySelector('[data-bundle-variant]');
      if (variantSelect) {
        variantSelect.value = entry.id;
        this.syncCardPrice(card);
      }
    });

    // If nothing survived, clear the stored payload.
    if (this.selection.size === 0) {
      try { sessionStorage.removeItem(this.storageKey); } catch (e) {}
    }
  }

  findCardForVariant(variantId) {
    const cards = Array.from(this.querySelectorAll('[data-bundle-product]'));
    return cards.find((card) => {
      if (card.dataset.variantId === String(variantId)) return true;
      const select = card.querySelector('[data-bundle-variant]');
      if (!select) return false;
      return Array.from(select.options).some((o) => o.value === String(variantId));
    }) || null;
  }

  // ---- Mobile bar visibility -------------------------------------------
  // Three rules, in order of precedence:
  //   1. No items selected  -> no bar (nothing to summarise).
  //   2. Customer dismissed -> no bar, but a small "N in bundle" pill so
  //      their picks are never lost. Dismissal is NOT the same as clearing.
  //   3. Section scrolled out of view -> no bar. The builder is only relevant
  //      while its own section is on screen; following the customer down the
  //      whole page is what makes these bars feel like nagging.
  bindBarVisibility() {
    this.barDismissed = false;
    this.inView = true;

    const dismiss = this.querySelector('[data-bundle-bar-dismiss]');
    if (dismiss) {
      dismiss.addEventListener('click', () => {
        this.barDismissed = true;
        this.closeSheet();
        this.render();
      });
    }

    const reopen = this.querySelector('[data-bundle-reopen]');
    if (reopen) {
      reopen.addEventListener('click', () => {
        this.barDismissed = false;
        this.render();
      });
    }

    // Track whether the builder is on screen.
    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          this.inView = entry.isIntersecting;
          this.render();
        });
      }, { rootMargin: '0px 0px -10% 0px' });
      this.observer.observe(this);
    }
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
    document.body.style.overflow = '';
  }

  // ---- Mobile bottom sheet ---------------------------------------------
  bindMobileSheet() {
    this.sheet = this.querySelector('[data-bundle-sheet]');
    this.sheetBody = this.querySelector('[data-bundle-sheet-body]');
    if (!this.sheet) return;

    this.querySelectorAll('[data-bundle-sheet-open]').forEach((btn) => {
      btn.addEventListener('click', () => this.openSheet());
    });
    this.querySelectorAll('[data-bundle-sheet-close]').forEach((btn) => {
      btn.addEventListener('click', () => this.closeSheet());
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.sheet && !this.sheet.hidden) this.closeSheet();
    });
  }

  openSheet() {
    if (!this.sheet) return;
    // Mirror the live summary groups into the sheet.
    const source = this.querySelector('.product-bundle__summary-groups');
    if (source && this.sheetBody) this.sheetBody.innerHTML = source.innerHTML;
    this.sheet.hidden = false;
    document.body.style.overflow = 'hidden';
    const toggle = this.querySelector('[data-bundle-sheet-open]');
    if (toggle) toggle.setAttribute('aria-expanded', 'true');
  }

  closeSheet() {
    if (!this.sheet) return;
    this.sheet.hidden = true;
    document.body.style.overflow = '';
    const toggle = this.querySelector('[data-bundle-sheet-open]');
    if (toggle) toggle.setAttribute('aria-expanded', 'false');
  }

  // ---- Group tabs -------------------------------------------------------
  bindTabs() {
    this.tabs = Array.from(this.querySelectorAll('[data-bundle-tab]'));
    this.panels = Array.from(this.querySelectorAll('[data-bundle-panel]'));

    this.tabs.forEach((tab) => {
      tab.addEventListener('click', () => this.activateGroup(tab.dataset.bundleTab));
    });

    // Prev/next arrows step through groups.
    const prev = this.querySelector('[data-bundle-prev]');
    const next = this.querySelector('[data-bundle-next]');
    if (prev) prev.addEventListener('click', () => this.stepGroup(-1));
    if (next) next.addEventListener('click', () => this.stepGroup(1));
  }

  activateGroup(index) {
    this.tabs.forEach((tab) => {
      const active = tab.dataset.bundleTab === String(index);
      tab.classList.toggle('is-active', active);
      tab.setAttribute('aria-selected', active ? 'true' : 'false');
    });
    this.panels.forEach((panel) => {
      panel.classList.toggle('is-active', panel.dataset.bundlePanel === String(index));
    });
  }

  stepGroup(direction) {
    const activeIndex = this.tabs.findIndex((t) => t.classList.contains('is-active'));
    const nextIndex = Math.min(Math.max(activeIndex + direction, 0), this.tabs.length - 1);
    if (this.tabs[nextIndex]) this.activateGroup(this.tabs[nextIndex].dataset.bundleTab);
  }

  // ---- Variant selection ------------------------------------------------
  bindVariantPickers() {
    this.querySelectorAll('[data-bundle-variant]').forEach((select) => {
      select.addEventListener('change', () => {
        const card = select.closest('[data-bundle-product]');
        this.syncCardPrice(card);
        // If this product was already selected, move the selection to the new variant.
        const previous = card.dataset.selectedVariant;
        if (previous && this.selection.has(previous)) {
          const entry = this.selection.get(previous);
          this.selection.delete(previous);
          const newId = this.currentVariantId(card);
          this.selection.set(newId, { ...entry, id: newId, price: this.currentVariantPrice(card) });
          card.dataset.selectedVariant = newId;
          this.render();
        }
      });
    });
  }

  currentVariantId(card) {
    const select = card.querySelector('[data-bundle-variant]');
    return select ? select.value : card.dataset.variantId;
  }

  currentVariantPrice(card) {
    const select = card.querySelector('[data-bundle-variant]');
    if (select) {
      const option = select.options[select.selectedIndex];
      return Number(option.dataset.price) || 0;
    }
    return Number(card.dataset.price) || 0;
  }

  syncCardPrice(card) {
    const priceEl = card.querySelector('[data-bundle-price]');
    if (!priceEl) return;
    priceEl.textContent = this.formatMoney(this.currentVariantPrice(card));
  }

  // ---- Quantity ---------------------------------------------------------
  bindQuantitySteppers() {
    this.addEventListener('click', (event) => {
      const inc = event.target.closest('[data-bundle-qty-increase]');
      const dec = event.target.closest('[data-bundle-qty-decrease]');
      if (!inc && !dec) return;

      const wrapper = (inc || dec).closest('[data-bundle-qty]');
      const input = wrapper.querySelector('[data-bundle-qty-input]');
      const card = wrapper.closest('[data-bundle-product]');
      const min = this.onceOnly ? 0 : 0;
      const max = this.onceOnly ? 1 : 99;

      let value = Number(input.value) || 0;
      value = inc ? Math.min(value + 1, max) : Math.max(value - 1, min);
      input.value = value;

      this.setQuantity(card, value);
    });
  }

  setQuantity(card, quantity) {
    const id = this.currentVariantId(card);
    if (quantity > 0) {
      // Adding something after dismissing means they've re-engaged, so bring
      // the summary back rather than leaving them with just the pill.
      this.barDismissed = false;
      this.selection.set(id, {
        id: id,
        quantity: quantity,
        price: this.currentVariantPrice(card),
        title: card.dataset.title || '',
        group: card.dataset.group || '0',
        groupTitle: card.dataset.groupTitle || '',
      });
      card.dataset.selectedVariant = id;
      card.classList.add('is-selected');
    } else {
      this.selection.delete(id);
      card.classList.remove('is-selected');
      delete card.dataset.selectedVariant;
    }
    this.render();
  }

  // ---- "Add to bundle" buttons -----------------------------------------
  bindAddButtons() {
    this.querySelectorAll('[data-bundle-add]').forEach((button) => {
      button.addEventListener('click', () => {
        const card = button.closest('[data-bundle-product]');
        const id = this.currentVariantId(card);
        const isSelected = this.selection.has(id);

        if (isSelected && this.onceOnly) {
          this.setQuantity(card, 0);
        } else {
          const qtyInput = card.querySelector('[data-bundle-qty-input]');
          const current = qtyInput ? Number(qtyInput.value) || 0 : 0;
          const next = this.onceOnly ? 1 : current + 1;
          if (qtyInput) qtyInput.value = next;
          this.setQuantity(card, next);
        }

        // Brief press animation so the click always registers visually, even
        // when the item was already in the bundle.
        button.classList.remove('is-pulsing');
        // Force reflow so the animation restarts on rapid repeat clicks.
        void button.offsetWidth;
        button.classList.add('is-pulsing');
      });
    });
  }

  // Reflect the selected state on each once-only add button: label and styling.
  updateAddButtons() {
    if (!this.onceOnly) return;
    this.querySelectorAll('[data-bundle-add]').forEach((button) => {
      const card = button.closest('[data-bundle-product]');
      const id = this.currentVariantId(card);
      const selected = this.selection.has(id);
      button.classList.toggle('is-added', selected);
      button.setAttribute('aria-pressed', selected ? 'true' : 'false');
      button.textContent = selected ? this.addedLabel : this.addLabel;
    });
  }

  // ---- Totals and summary ----------------------------------------------
  get totalItems() {
    let count = 0;
    this.selection.forEach((entry) => { count += entry.quantity; });
    return count;
  }

  get subtotal() {
    let sum = 0;
    this.selection.forEach((entry) => { sum += entry.price * entry.quantity; });
    return sum;
  }

  currentTier() {
    let reached = null;
    this.tiers.forEach((tier) => {
      if (this.totalItems >= tier.min) reached = tier;
    });
    return reached;
  }

  nextTier() {
    return this.tiers.find((tier) => this.totalItems < tier.min) || null;
  }

  render() {
    // Persist on every change so the bundle survives page navigation.
    this.saveSelection();

    // Reflect selected state on the once-only add buttons.
    this.updateAddButtons();

    // Per-group counts
    this.querySelectorAll('[data-group-count]').forEach((el) => {
      const group = el.dataset.groupCount;
      let count = 0;
      this.selection.forEach((entry) => {
        if (entry.group === group) count += entry.quantity;
      });
      el.textContent = count;
      const row = el.closest('[data-group-row]');
      if (row) row.classList.toggle('is-filled', count > 0);
    });

    // Selected item lists per group
    this.querySelectorAll('[data-group-items]').forEach((el) => {
      const group = el.dataset.groupItems;
      const items = [];
      this.selection.forEach((entry) => {
        if (entry.group === group) items.push(entry);
      });
      if (items.length === 0) {
        el.innerHTML = '<p class="bundle-summary__empty">No product chosen</p>';
      } else {
        el.innerHTML = items.map((item) => (
          '<p class="bundle-summary__line"><span>' + this.escape(item.title) + '</span>' +
          '<span>&times;' + item.quantity + '</span></p>'
        )).join('');
      }
    });

    // Subtotal
    const totalEl = this.querySelector('[data-bundle-total]');
    if (totalEl) totalEl.textContent = this.formatMoney(this.subtotal);

    // Mobile bar visibility — see bindBarVisibility for the rules.
    const hasItems = this.totalItems > 0;
    const showBar = hasItems && !this.barDismissed && this.inView;
    const showReopen = hasItems && this.barDismissed && this.inView;

    const mobileBar = this.querySelector('[data-bundle-mobile-bar]');
    if (mobileBar) mobileBar.hidden = !showBar;
    this.classList.toggle('has-mobile-bar', showBar);

    const reopen = this.querySelector('[data-bundle-reopen]');
    if (reopen) {
      reopen.hidden = !showReopen;
      const reopenCount = this.querySelector('[data-reopen-count]');
      if (reopenCount) reopenCount.textContent = this.totalItems;
    }

    const mobileTotal = this.querySelector('[data-bundle-total-mobile]');
    if (mobileTotal) mobileTotal.textContent = this.formatMoney(this.subtotal);
    const mobileCount = this.querySelector('[data-mobile-count]');
    if (mobileCount) mobileCount.textContent = this.totalItems;

    // If the sheet is open, keep its contents live.
    if (this.sheet && !this.sheet.hidden && this.sheetBody) {
      const source = this.querySelector('.product-bundle__summary-groups');
      if (source) this.sheetBody.innerHTML = source.innerHTML;
    }

    // Tier progress message
    const tierEl = this.querySelector('[data-bundle-tier-message]');
    const tierElMobile = this.querySelector('[data-bundle-tier-message-mobile]');
    const next = this.nextTier();
    const current = this.currentTier();
    let tierText = '';
    if (next) {
      const remaining = next.min - this.totalItems;
      tierText = 'Add ' + remaining + ' more to get ' + next.label + '.';
    } else if (current) {
      tierText = current.label + ' unlocked.';
    }
    if (tierEl) tierEl.textContent = tierText;
    if (tierElMobile) tierElMobile.textContent = tierText;

    // Tier progress bar
    const bar = this.querySelector('[data-bundle-progress]');
    if (bar && this.tiers.length) {
      const maxTier = this.tiers[this.tiers.length - 1].min;
      const pct = Math.min((this.totalItems / maxTier) * 100, 100);
      bar.style.setProperty('--bundle-progress', pct + '%');
      this.querySelectorAll('[data-tier-marker]').forEach((marker) => {
        marker.classList.toggle('is-reached', this.totalItems >= Number(marker.dataset.tierMarker));
      });
    }

    // Submit availability (desktop + mobile)
    const meetsMin = this.totalItems >= this.minItems;
    [
      this.querySelector('[data-bundle-submit]'),
      this.querySelector('[data-bundle-submit-mobile]'),
    ].forEach((button) => {
      if (!button) return;
      button.disabled = !meetsMin;
      button.classList.toggle('is-disabled', !meetsMin);
    });
  }

  // ---- Add all to cart --------------------------------------------------
  bindSubmit() {
    const buttons = [
      this.querySelector('[data-bundle-submit]'),
      this.querySelector('[data-bundle-submit-mobile]'),
    ].filter(Boolean);
    if (buttons.length === 0) return;

    buttons.forEach((submit) => {
      submit.addEventListener('click', async () => {
        if (this.selection.size === 0) return;

        // Each bundle line carries properties so the cart, checkout, and the
        // merchant's order detail can all tell bundle items apart from items
        // the customer added normally.
        //   - "Bundle" (visible)  -> customer-facing tag in cart/checkout
        //   - "_bundle_id"        -> hidden, groups this specific bundle build
        //   - "_bundle_step"      -> hidden, which step the item came from
        const bundleId = this.bundleLabel + '-' + Date.now().toString(36);
        const items = [];
        this.selection.forEach((entry) => {
          const properties = {};
          properties[this.bundlePropertyName] = this.bundleLabel;
          properties._bundle_id = bundleId;
          if (entry.groupTitle) properties._bundle_step = entry.groupTitle;

          items.push({
            id: Number(entry.id),
            quantity: entry.quantity,
            properties: properties,
          });
        });

        const errorEl = this.querySelector('[data-bundle-error]');
        if (errorEl) errorEl.textContent = '';
        buttons.forEach((b) => b.setAttribute('aria-busy', 'true'));

        try {
          const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: items }),
          });

          if (!response.ok) {
            const data = await response.json().catch(() => ({}));
            throw new Error(data.description || data.message || 'Could not add bundle to cart.');
          }

          // Apply the merchant's discount code, if one is configured. Shopify
          // owns the actual discount; this just applies the existing code.
          if (this.discountCode) {
            try {
              await fetch('/discount/' + encodeURIComponent(this.discountCode), { method: 'GET' });
            } catch (e) {
              // Non-fatal: items are in the cart even if the code fails to apply.
            }
          }

          this.closeSheet();
          document.dispatchEvent(new CustomEvent('cart:updated'));

          // Open the cart drawer using the theme's existing trigger.
          const drawerTrigger = document.querySelector('[data-cart-open]');
          if (drawerTrigger) drawerTrigger.click();

          // Reset the builder after a successful add. The items live in the
          // real cart now, so the saved draft must go.
          this.selection.clear();
          this.barDismissed = false;
          try { sessionStorage.removeItem(this.storageKey); } catch (e) {}
          this.querySelectorAll('[data-bundle-qty-input]').forEach((i) => { i.value = 0; });
          this.querySelectorAll('[data-bundle-product]').forEach((c) => c.classList.remove('is-selected'));
          this.render();
        } catch (error) {
          if (errorEl) errorEl.textContent = error.message;
          const mobileError = this.querySelector('[data-bundle-error-mobile]');
          if (mobileError) mobileError.textContent = error.message;
        } finally {
          buttons.forEach((b) => b.removeAttribute('aria-busy'));
        }
      });
    });
  }

  // ---- Helpers ----------------------------------------------------------
  formatMoney(cents) {
    const amount = (cents / 100).toFixed(2);
    if (this.moneyFormat.indexOf('{{') !== -1) {
      return this.moneyFormat.replace(/\{\{\s*amount\s*\}\}/, amount);
    }
    return this.currency + amount;
  }

  escape(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}

customElements.define('product-bundle', ProductBundle);
